import { LearnerPretestStatus, PretestOutcome, PretestSkipRule } from '~/data';
import { DraftQuestion } from './pretestDraft';

export interface PretestChapterResult {
  chapterId: string;
  outcome: PretestOutcome;
  asked: number;
  correct: number;
}

export interface Pretest {
  status: LearnerPretestStatus;
  testableChapterCount: number;
  // Planned count while AVAILABLE, drawn count once started. Every authored question is asked.
  questionCount: number;
  // Whether the authored quiz covered every required chapter when the learner started. It describes the
  // quiz; only the training status says whether a pretest completed the course.
  fullCourseEligible: boolean;
  // True only when this assessment completed the course at submission, not after later lessons.
  completedCourse: boolean;
  // How answers decide the chapters skipped: the course's rule until started, then the one captured at
  // start. STRICT skips a chapter only when every answer about it is right, with no passing score.
  skipRule: PretestSkipRule;
  // The passing score captured at start (none under STRICT); the graded result once COMPLETED, misses included.
  passingScorePercent: number | null;
  scorePercent: number | null;
  correctCount: number | null;
  passed: boolean | null;
  // Empty until COMPLETED.
  results: PretestChapterResult[];
}

// The pretest itself lives with the course outline (NavigationSlice.pretest);
// this slice holds the learner's answers and the pretest actions.
export interface PretestSlice {
  // Option ids the learner picked, by question id. They stay on the client
  // until submitPretest: pretest answers never go through saveAnswers.
  pretestAnswers: Record<string, string[]>;
  // A pretest request is on its way. One goes at a time, so two results never race, and the
  // choices stay as sent until the server answers.
  pretestBusy: boolean;
  // Restore this tab's draft only for the recorded pretest and current draw.
  restorePretestAnswers(pretestId: string, questions: DraftQuestion[]): void;
  choosePretestOption(
    questionId: string,
    optionId: string,
    multiple: boolean
  ): void;
  // Each resolves to false (after telling the learner) when the request fails or the server
  // refuses. A failed submit keeps the answers, so the learner can send them again.
  startPretest(): Promise<boolean>;
  submitPretest(questionIds: string[]): Promise<boolean>;
  declinePretest(): Promise<boolean>;
  // Takes the pretest as the server has it now, as after another tab submitted or declined it.
  refreshPretest(): Promise<boolean>;
  // Where the learner goes once the pretest is behind them.
  pretestExitRoute(): string;
}
